import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../App';
import { Radio, X, Mic, Square, Send, Search, Trash2, Play, Pause, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';

const MAX_SECONDS = 30;

/**
 * PTTBroadcastModal
 * Selector múltiple de usuarios + grabador de nota de voz (hasta 30s) para envío broadcast.
 * Solo admin y profesor pueden usarlo (el backend valida).
 */
const PTTBroadcastModal = ({ open, onClose }) => {
  const { API, getImageUrl } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [recState, setRecState] = useState('idle'); // idle | recording | preview | uploading
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState(null);
  const [blobUrl, setBlobUrl] = useState('');
  const [playing, setPlaying] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const intervalRef = useRef(null);
  const audioElRef = useRef(null);

  // Cargar usuarios
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API}/ptt/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!resp.ok) throw new Error('No se pudo cargar la lista');
        const data = await resp.json();
        if (!cancelled) setUsers(data.users || []);
      } catch (e) {
        toast.error(e.message || 'Error cargando usuarios');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, API]);

  // Cleanup al cerrar
  useEffect(() => {
    if (open) return;
    stopRecording(true);
    setSelectedIds(new Set());
    setSearch('');
    setSeconds(0);
    setBlob(null);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl('');
    setRecState('idle');
  }, [open]);

  const toggleSelect = (uid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      if (next.size > 20) {
        toast.warning('Máximo 20 destinatarios por envío');
        return prev;
      }
      return next;
    });
  };

  const selectAll = () => {
    const filtered = filteredUsers();
    const next = new Set();
    for (const u of filtered) {
      if (next.size >= 20) break;
      next.add(u.id);
    }
    setSelectedIds(next);
  };

  const clearAll = () => setSelectedIds(new Set());

  const filteredUsers = () => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => {
      const full = `${u.nombre || ''} ${u.apellido || ''} ${u.email || ''}`.toLowerCase();
      return full.includes(q);
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' :
        (MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' :
         (MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : ''));
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRecRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        setBlob(b);
        setBlobUrl(URL.createObjectURL(b));
        setRecState('preview');
        try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_e) { /* noop */ }
      };
      rec.start();
      setRecState('recording');
      setSeconds(0);
      intervalRef.current = setInterval(() => {
        setSeconds(s => {
          const ns = s + 1;
          if (ns >= MAX_SECONDS) {
            stopRecording();
            return MAX_SECONDS;
          }
          return ns;
        });
      }, 1000);
    } catch (e) {
      toast.error('No pude acceder al micrófono. Concedé permisos.');
    }
  };

  const stopRecording = (silent = false) => {
    if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    try {
      if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
        mediaRecRef.current.stop();
      }
    } catch (_e) { /* noop */ }
    try { streamRef.current?.getTracks().forEach(t => t.stop()); } catch (_e) { /* noop */ }
    streamRef.current = null;
    if (silent) {
      setRecState('idle');
      setBlob(null);
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      setBlobUrl('');
    }
  };

  const discardRecording = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlob(null);
    setBlobUrl('');
    setSeconds(0);
    setRecState('idle');
    setPlaying(false);
  };

  const togglePlay = () => {
    if (!audioElRef.current) return;
    if (playing) {
      audioElRef.current.pause();
      setPlaying(false);
    } else {
      audioElRef.current.play();
      setPlaying(true);
    }
  };

  const send = async () => {
    if (!blob || selectedIds.size === 0) return;
    setRecState('uploading');
    try {
      const fd = new FormData();
      const ext = (blob.type.includes('mp4') ? 'm4a' : (blob.type.includes('ogg') ? 'ogg' : 'webm'));
      fd.append('audio', blob, `nota-voz.${ext}`);
      fd.append('recipient_ids', JSON.stringify(Array.from(selectedIds)));
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/ptt/broadcast`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Error enviando la nota');
      }
      const data = await resp.json();
      toast.success(`🎙️ Nota de voz enviada a ${data.recipients_total} destinatario${data.recipients_total !== 1 ? 's' : ''}`);
      onClose();
    } catch (e) {
      toast.error(e.message || 'Error enviando la nota de voz');
      setRecState('preview');
    }
  };

  if (!open) return null;

  const filtered = filteredUsers();
  const getInitials = (u) => `${(u.nombre || '')[0] || ''}${(u.apellido || '')[0] || ''}`.toUpperCase();

  return createPortal(
    <div
      className="fixed inset-0 z-[2147483641] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
      onClick={() => recState !== 'uploading' && onClose()}
      data-testid="ptt-broadcast-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[calc(100dvh-1.5rem)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-500 text-white px-5 py-3 flex items-center justify-between shrink-0">
          <h3 className="font-bold text-base flex items-center gap-2">
            <Radio className="w-5 h-5" /> Radio Difusión — Nota de voz
          </h3>
          <button
            onClick={onClose}
            disabled={recState === 'uploading'}
            className="text-white/80 hover:text-white p-1"
            aria-label="Cerrar"
            data-testid="ptt-broadcast-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {/* Selector múltiple */}
          <div className="p-4 border-b">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-semibold text-gray-700">Destinatarios ({selectedIds.size}/20)</span>
              <button
                onClick={selectAll}
                className="ml-auto text-xs text-orange-600 hover:text-orange-800 font-medium"
                data-testid="ptt-select-all"
              >Seleccionar visibles</button>
              <button
                onClick={clearAll}
                className="text-xs text-gray-500 hover:text-gray-700"
                data-testid="ptt-clear-all"
              >Limpiar</button>
            </div>
            <div className="relative mb-2">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                data-testid="ptt-search-input"
              />
            </div>
            <div className="max-h-40 overflow-y-auto border rounded-lg divide-y">
              {loading && (
                <div className="p-3 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <div className="p-3 text-center text-sm text-gray-500">Sin resultados</div>
              )}
              {!loading && filtered.map(u => {
                const checked = selectedIds.has(u.id);
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-3 p-2 cursor-pointer transition-colors ${checked ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
                    data-testid={`ptt-user-${u.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSelect(u.id)}
                      className="w-4 h-4 accent-orange-500"
                    />
                    {u.foto_perfil ? (
                      <img
                        src={getImageUrl ? getImageUrl(u.foto_perfil) : u.foto_perfil}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div
                      className={`w-8 h-8 rounded-full items-center justify-center text-xs font-bold text-white ${
                        u.rol === 'admin' ? 'bg-blue-500' : u.rol === 'profesor' ? 'bg-orange-500' : 'bg-gray-400'
                      }`}
                      style={{ display: u.foto_perfil ? 'none' : 'flex' }}
                    >
                      {getInitials(u)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{u.nombre} {u.apellido}</div>
                      <div className="text-xs text-gray-500 capitalize">{u.rol}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Grabador */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Mic className="w-4 h-4 text-gray-500" /> Nota de voz
              </span>
              <span className={`text-xs font-mono ${seconds >= MAX_SECONDS - 5 ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')} / 00:{MAX_SECONDS}
              </span>
            </div>

            {recState === 'idle' && (
              <button
                onClick={startRecording}
                className="w-full py-4 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-transform active:scale-95"
                data-testid="ptt-start-record"
              >
                <Mic className="w-5 h-5" /> Grabar nota (hasta {MAX_SECONDS}s)
              </button>
            )}

            {recState === 'recording' && (
              <button
                onClick={() => stopRecording()}
                className="w-full py-4 bg-red-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 animate-pulse"
                data-testid="ptt-stop-record"
              >
                <Square className="w-5 h-5" /> Detener grabación · {seconds}s
              </button>
            )}

            {recState === 'preview' && blobUrl && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-xl">
                  <button
                    onClick={togglePlay}
                    className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600"
                    data-testid="ptt-preview-play"
                  >
                    {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-orange-800">Vista previa · {seconds}s</div>
                    <audio
                      ref={audioElRef}
                      src={blobUrl}
                      onEnded={() => setPlaying(false)}
                      className="w-full mt-1"
                      controls
                      style={{ height: 28 }}
                    />
                  </div>
                  <button
                    onClick={discardRecording}
                    className="w-9 h-9 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center"
                    title="Descartar y regrabar"
                    data-testid="ptt-discard"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {recState === 'uploading' && (
              <div className="text-center py-4 text-orange-600 font-medium flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" /> Enviando...
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-3 flex items-center justify-between gap-2 border-t shrink-0">
          <span className="text-xs text-gray-500">
            {selectedIds.size} seleccionado{selectedIds.size !== 1 ? 's' : ''}
          </span>
          <button
            onClick={send}
            disabled={recState !== 'preview' || selectedIds.size === 0}
            className="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="ptt-broadcast-send"
          >
            <Send className="w-4 h-4" /> Enviar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PTTBroadcastModal;
